// Helpers for scrolling with lazy-loaded content and accessibility focus management. Used for same-page links with IntersectionObserver components.

import { FOCUSABLE_SELECTOR, isElementVisible } from './a11y/focusable-elements';
import { scrollTo } from './general';

const DEFAULT_POLL_MAX_RETRIES = 10; // 10 × 100ms = 1 second max wait (for slow loading)
const DEFAULT_POLL_INTERVAL_MS = 100;
const DEFAULT_POSITION_STABILITY_THRESHOLD_PX = 10;
const DEFAULT_POSITION_CHECK_DELAY_MS = 200;
const DEFAULT_STABILITY_MAX_CHECKS = 10; // Maximum iterations to prevent infinite loops
const SCROLL_ANIMATION_DURATION_MS = 600;

interface ScrollToLazyOptions {
  focusSelector?: string;
}

export const pollForElement = <T extends Element>(
  getElement: () => T | null,
  maxRetries = DEFAULT_POLL_MAX_RETRIES,
  interval = DEFAULT_POLL_INTERVAL_MS,
): Promise<T | null> => {
  return new Promise((resolve) => {
    let retries = 0;
    const poll = () => {
      const element = getElement();
      if (element) {
        resolve(element);
      } else if (retries >= maxRetries) {
        resolve(null); // Silently fail
      } else {
        retries++;
        setTimeout(poll, interval);
      }
    };
    poll();
  });
};

const waitForStablePosition = (
  element: Element,
  threshold = DEFAULT_POSITION_STABILITY_THRESHOLD_PX,
  checkDelay = DEFAULT_POSITION_CHECK_DELAY_MS,
  maxChecks = DEFAULT_STABILITY_MAX_CHECKS,
): Promise<void> => {
  return new Promise((resolve) => {
    const checkStability = (checksRemaining: number) => {
      if (checksRemaining <= 0) {
        resolve();
        return;
      }

      const initialTop = element.getBoundingClientRect().top;

      setTimeout(() => {
        const currentTop = element.getBoundingClientRect().top;
        const positionDiff = Math.abs(currentTop - initialTop);

        if (positionDiff > threshold) {
          checkStability(checksRemaining - 1);
        } else {
          resolve();
        }
      }, checkDelay);
    };

    checkStability(maxChecks);
  });
};

const isElementFocusable = (element: HTMLElement): boolean => {
  // Elements with tabindex="-1" can receive programmatic focus (even though not in tab order)
  const hasTabIndex = element.hasAttribute('tabindex');
  const matchesFocusableSelector = element.matches(FOCUSABLE_SELECTOR);

  return (hasTabIndex || matchesFocusableSelector) && isElementVisible(element);
};

export const scrollToLazy = async (selector: string, options?: ScrollToLazyOptions): Promise<void> => {
  const { focusSelector = selector } = options || {};

  const section = await pollForElement(() => document.querySelector(selector));
  if (!section) {
    return;
  }

  await waitForStablePosition(section);

  scrollTo(selector);

  await new Promise((resolve) => setTimeout(resolve, SCROLL_ANIMATION_DURATION_MS));

  const focusTarget = await pollForElement(() => document.querySelector(focusSelector) as HTMLElement);
  if (!focusTarget) {
    return;
  }

  if (!isElementFocusable(focusTarget)) {
    return;
  }

  const targetRect = focusTarget.getBoundingClientRect();
  const isInViewport = targetRect.top >= 0 && targetRect.bottom <= window.innerHeight;

  if (!isInViewport && targetRect.top > window.innerHeight) {
    window.scrollTo({
      behavior: 'smooth',
      top: window.scrollY + (targetRect.top - window.innerHeight / 3),
      left: 0,
    });
    await new Promise((resolve) => setTimeout(resolve, SCROLL_ANIMATION_DURATION_MS));
  }

  focusTarget.focus({ preventScroll: true });
};
